// Copyright (c) 2024 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup } from "react-bootstrap";

import { ActionType, IAction, IActionTypeSelectGuiItem, raiseAction } from "actions";
import { IState } from "states/states";
import { DataState } from "types";
import { ModalPanel } from "states/guistates";
import { getSiteSelection, toDropdownItems } from "guiutils";
import { Category } from "states/cachedstates";

import FilterBadge from "view/comp/FilterBadge";
import SaveFeedback from "view/comp/SaveFeedback";
import Dropdown from "view/comp/Dropdown";
import TariffCircuitListModel from "model/tariffcircuitlist";

import "./ModalFilter.scss";

interface IModalTariffsFilterProps {
  isOpen: boolean;
  dataState: DataState;
  category?: string | null;
  categories: (Category | undefined)[];
}

const mapStateToProps = (state: IState) => {
  const selection = getSiteSelection(state);
  const tcModel = new TariffCircuitListModel(
    state.para.parameter?.tariffline ?? [],
    state.para.parameter?.energymeter ?? [],
  );

  const props: IModalTariffsFilterProps = {
    isOpen: state.gui.modal.panel === ModalPanel.FILTER_SELECTION,
    dataState: state.gui.modal.dataState,
    category: selection?.category,
    categories: tcModel.getCategories(selection?.energySource),
  };
  return props;
};

interface IModalTariffsFilterState {
  wasOpen: boolean;
  category: string | undefined | null;
}

interface IModalTariffsFilterDispatch {
  onCancelClick: () => void;
  onFilterChanged: (props: IModalTariffsFilterProps, state: IModalTariffsFilterState) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IModalTariffsFilterDispatch = {
    onCancelClick: () => {
      dispatch(raiseAction(ActionType.ACT_MODAL_CANCEL));
    },
    onFilterChanged: (props: IModalTariffsFilterProps, state: IModalTariffsFilterState) => {
      // store and close panel
      const payload: IActionTypeSelectGuiItem = {
        selection: state.category,
      };
      dispatch(raiseAction(ActionType.ACT_SELECT_CATEGORY, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  categories: { id: "Categories" },
  allCategories: { id: "All categories" },
  selectCategory: { id: "Select category" },
  noCategories: { id: "Without mapping" },
  filterPreamble: { id: "Filter" },
});

class ModalTariffsFilterComponent extends React.Component<
  IModalTariffsFilterProps & IModalTariffsFilterDispatch,
  IModalTariffsFilterState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  state: IModalTariffsFilterState = {
    wasOpen: false,
    category: undefined,
  };

  componentDidUpdate(
    prevProps: Readonly<IModalTariffsFilterProps & IModalTariffsFilterDispatch>,
    prevState: Readonly<IModalTariffsFilterState>,
    snapshot?: any,
  ): void {
    if (this.props.isOpen !== this.state.wasOpen) {
      this.setState({
        wasOpen: this.props.isOpen,
        category: this.props.category,
      });
    }
  }

  public render() {
    if (!this.props.isOpen) {
      return <></>; // avoid rendering of inexisting data
    }

    return (
      <Modal
        className="modal_filter modal_filter-tariffs"
        show={this.props.isOpen}
        onHide={this.props.onCancelClick}
      >
        <ModalHeader>
          {this.state.category !== undefined && (
            <div className="badges">
              <FilterBadge
                text={this.state.category ?? this.context.formatMessage(messages.noCategories)}
                type={this.context.formatMessage(messages.filterPreamble)}
                onClear={() => {
                  this.setState({ category: undefined });
                }}
              />
            </div>
          )}
        </ModalHeader>
        <ModalBody>
          <>
            <div className="headline mb-3">
              <h5>
                <FormattedMessage id="Filter" />
              </h5>
              <div>
                <FormattedMessage id="Select the needed filters." />
              </div>
            </div>
            {this.renderCategories()}
          </>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between mt-3 mx-4">
          <Button
            id="clearFilters"
            variant="secondary"
            className="me-1"
            onClick={() => this.setState({ category: undefined })}
          >
            <FormattedMessage id="Clear All" />
          </Button>
          <SaveFeedback dataState={this.props.dataState} />
          <div className="ms-1">
            <Button
              id="cancelTrend"
              variant="secondary"
              className="me-1"
              onClick={this.props.onCancelClick}
            >
              <FormattedMessage id="Cancel" />
            </Button>
            <Button
              id="useFilter"
              variant="primary"
              className="ms-2"
              onClick={this.props.onFilterChanged.bind(this, this.props, this.state)}
              disabled={this.props.dataState === DataState.SAVE_REQ}
            >
              <FormattedMessage id="Accept" />
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    );
  }

  private renderCategories() {
    const categoryItems = toDropdownItems(
      this.props.categories ?? [],
      "categories",
      (name, value) => {
        this.setState({ category: value });
      },
      (value) =>
        value === null ? this.context.formatMessage(messages.noCategories)
        : value === undefined ? this.context.formatMessage(messages.allCategories)
        : value,
    );

    return (
      <>
        <FormGroup className="category-filter">
          <label className="category-filter-label">
            <FormattedMessage id="Category" />
          </label>
          <Dropdown
            key={"categories"}
            items={categoryItems}
            caption={this.context.formatMessage(messages.selectCategory)}
            curItem={
              this.state.category === null ? this.context.formatMessage(messages.noCategories)
              : this.state.category === undefined ?
                this.context.formatMessage(messages.allCategories)
              : this.state.category
            }
          />
        </FormGroup>
      </>
    );
  }
}

const ModaTariffsFilterItem = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ModalTariffsFilterComponent);

export default ModaTariffsFilterItem;
